import React, { useEffect, useState } from 'react';
import { Alert, Badge, Card, Col, Row, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import axios from 'helpers/api';
import CommonBreadCrumb from 'components/commonbreadcrumb/CommonBreadCrumb';
import paths from 'routes/paths';
import { getAtHomePatients } from 'helpers/isSharedLivingActive';

const formatDate = date =>
  date ? moment(date, ['YYYY-MM-DD', 'MM/DD/YYYY', moment.ISO_8601]).format('MM/DD/YY') : '—';

const getGenderName = gender => {
  if (gender === 1 || gender === '1') return 'Male';
  if (gender === 2 || gender === '2') return 'Female';
  return 'Other';
};

const InfoRow = ({ label, value }) => (
  <div className="mb-2">
    <div className="fs-11 text-600">{label}</div>
    <div className="fw-semibold">{value || '—'}</div>
  </div>
);

/**
 * UI-035/036/041: read-only patient profile for a SharedLiving caregiver.
 * Shows demographics, allergies, insurance, ICD codes and active prescriptions.
 * If the patient is no longer in the caregiver's active assignment list, a
 * historical-access banner is shown (SL-075).
 */
const MyPatientDetail = () => {
  const { patientId } = useParams();
  const [patient, setPatient] = useState(null);
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const isHistorical = !getAtHomePatients()
    .map(String)
    .includes(String(patientId));

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const tokenStr = localStorage.getItem('accessToken');
        const headers = { Authorization: `Bearer ${tokenStr}` };

        const patientRes = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/patients/get-patient/${patientId}`,
          { headers }
        );
        setPatient(patientRes.data?.patients?.data || null);

        // Active prescriptions (read-only). Endpoint is tolerant — failures
        // just leave the section empty.
        try {
          const presRes = await axios.get(
            `${process.env.REACT_APP_SERVER_URL}/prescriptions/list-prescriptions/${patientId}`,
            { headers }
          );
          const list =
            presRes.data?.prescriptions?.data ||
            presRes.data?.data ||
            presRes.data?.prescriptions ||
            [];
          setPrescriptions(Array.isArray(list) ? list : []);
        } catch {
          setPrescriptions([]);
        }
      } catch (error) {
        toast.error('Unable to load patient');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [patientId]);

  const patientName = patient
    ? `${patient.last_name || ''}, ${patient.first_name || ''}`
    : 'Patient';

  const breadcrumbItems = [
    { name: 'Dashboard', href: '/main', isActive: false },
    { name: 'My Patients', href: paths.caregiverMyPatients, isActive: false },
    { name: patientName, href: '#', isActive: true }
  ];

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!patient) {
    return (
      <>
        <CommonBreadCrumb breadcrumbItems={breadcrumbItems} />
        <Alert variant="warning" className="mt-3">
          This patient is no longer in your care or could not be found.
        </Alert>
        <Link to={paths.caregiverMyPatients}>Back to My Patients</Link>
      </>
    );
  }

  return (
    <>
      <CommonBreadCrumb breadcrumbItems={breadcrumbItems} />
      {isHistorical && (
        <Alert variant="info" className="mt-3">
          <FontAwesomeIcon icon="info-circle" className="me-2" />
          You are viewing historical data for a patient no longer in your care.
        </Alert>
      )}
      <Row className="g-3 mt-1">
        <Col xs="12" lg="5">
          <Card className="h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{patientName}</h5>
              <Badge bg={Number(patient.is_active) === 1 ? 'success' : 'secondary'}>
                {Number(patient.is_active) === 1 ? 'Active' : 'Inactive'}
              </Badge>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col xs="6"><InfoRow label="DOB" value={formatDate(patient.dob)} /></Col>
                <Col xs="6"><InfoRow label="Gender" value={getGenderName(patient.gender)} /></Col>
                <Col xs="6"><InfoRow label="Facility" value={patient.facility_name} /></Col>
                <Col xs="6"><InfoRow label="Admit Date" value={formatDate(patient.admit_date)} /></Col>
                <Col xs="12"><InfoRow label="Allergies" value={patient.allergies} /></Col>
                <Col xs="6"><InfoRow label="Insurance #" value={patient.insurance_no} /></Col>
                <Col xs="6"><InfoRow label="Insurance Group" value={patient.insurance_group_no} /></Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Col xs="12" lg="7">
          <Card className="h-100">
            <Card.Header>
              <h5 className="mb-0">Active Prescriptions</h5>
            </Card.Header>
            <Card.Body>
              {prescriptions.length > 0 ? (
                <Table responsive size="sm" striped className="fs-10 mb-0">
                  <thead className="bg-200">
                    <tr>
                      <th>Drug</th>
                      <th>Dose / Sig</th>
                      <th>Frequency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prescriptions.map((rx, idx) => (
                      <tr key={rx.id || idx}>
                        <td>{rx.drug_name || rx.drug || rx.medication_name || '—'}</td>
                        <td>{rx.sig || rx.dose || rx.directions || '—'}</td>
                        <td>{rx.frequency || rx.frequency_name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center text-600 py-4">No active prescriptions.</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default MyPatientDetail;
